/**
 * React Query hooks for server state management.
 *
 * Wraps useLogisticsApi() fetch functions with @tanstack/react-query
 * for automatic caching, deduplication, and background refetching.
 */

import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import useLogisticsApi, { CourierRoutesResult, WarehouseOutgoingResult } from './use-logistics-api';
import { Order, BoxStatus, PartnerUser, DpdShipmentSummary } from '../types/logistics';

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const queryKeys = {
  courierRoutes: (date: string) => ['courier-routes', date] as const,
  warehouseIncoming: (date: string) => ['warehouse-incoming', date] as const,
  warehouseOutgoing: (date: string) => ['warehouse-outgoing', date] as const,
  partnerUsers: (partnerId: number) => ['partner-users', partnerId] as const,
  boxStatuses: () => ['box-statuses'] as const,
};

// ─── Stale Times ─────────────────────────────────────────────────────────────

const STALE = {
  courierRoutes: 2 * 60 * 1000,    // 2 min — route data changes moderately
  warehouseData: 60 * 1000,         // 1 min — warehouse ops change frequently
  partnerUsers: 10 * 60 * 1000,     // 10 min — driver list rarely changes
  boxStatuses: 30 * 60 * 1000,      // 30 min — reference data
};

// ─── Query Hooks ─────────────────────────────────────────────────────────────

export function useCourierRoutesQuery(date: string, enabled = true) {
  const { getCourierRoutes } = useLogisticsApi();

  return useQuery<CourierRoutesResult>({
    queryKey: queryKeys.courierRoutes(date),
    queryFn: () => getCourierRoutes(date),
    staleTime: STALE.courierRoutes,
    enabled,
  });
}

export function useWarehouseIncomingQuery(date: string, enabled = true) {
  const { getWarehouseIncoming } = useLogisticsApi();

  return useQuery<Order[]>({
    queryKey: queryKeys.warehouseIncoming(date),
    queryFn: () => getWarehouseIncoming(date),
    staleTime: STALE.warehouseData,
    enabled,
  });
}

export function useWarehouseOutgoingQuery(date: string, enabled = true) {
  const { getWarehouseOutgoing } = useLogisticsApi();

  return useQuery<WarehouseOutgoingResult>({
    queryKey: queryKeys.warehouseOutgoing(date),
    queryFn: () => getWarehouseOutgoing(date),
    staleTime: STALE.warehouseData,
    enabled,
  });
}

/**
 * Fetches warehouse outgoing data for multiple dates in parallel.
 * Deduplicates orders across dates and merges DPD shipment data.
 * Used by Dispatch to show orders up to 3 days ahead.
 */
export function useWarehouseOutgoingRangeQuery(dates: string[], enabled = true) {
  const { getWarehouseOutgoing } = useLogisticsApi();

  const queryResults = useQueries({
    queries: dates.map(date => ({
      queryKey: queryKeys.warehouseOutgoing(date),
      queryFn: () => getWarehouseOutgoing(date),
      staleTime: STALE.warehouseData,
      enabled,
    })),
  });

  const isLoading = queryResults.every(q => q.isLoading);
  const isSuccess = queryResults.some(q => q.isSuccess);
  const error = (queryResults.find(q => q.error)?.error as Error) ?? null;

  const memoKey = queryResults.map(q => q.dataUpdatedAt).join(',');
  const data = useMemo((): WarehouseOutgoingResult | null => {
    const loaded = queryResults.filter(q => q.data).map(q => q.data!);
    if (loaded.length === 0) return null;

    const seenOrderIds = new Set<number>();
    const allOrders: Order[] = [];
    const allDpdShipments: Record<number, DpdShipmentSummary> = {};

    for (const result of loaded) {
      for (const order of result.orders) {
        if (!seenOrderIds.has(order.id)) {
          seenOrderIds.add(order.id);
          allOrders.push(order);
        }
      }
      Object.assign(allDpdShipments, result.dpdShipments);
    }

    return { orders: allOrders, dpdShipments: allDpdShipments };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoKey]);

  return { data, isLoading, isSuccess, error };
}

export function usePartnerUsersQuery(partnerId: number | undefined, enabled = true) {
  const { getPartnerUsers } = useLogisticsApi();

  return useQuery<PartnerUser[]>({
    queryKey: queryKeys.partnerUsers(partnerId!),
    queryFn: () => getPartnerUsers(partnerId!),
    staleTime: STALE.partnerUsers,
    enabled: enabled && partnerId != null,
  });
}

export function useBoxStatusesQuery(enabled = true) {
  const { getStatuses } = useLogisticsApi();

  return useQuery<BoxStatus[]>({
    queryKey: queryKeys.boxStatuses(),
    queryFn: () => getStatuses(),
    staleTime: STALE.boxStatuses,
    enabled,
  });
}

// ─── Cache Invalidation ─────────────────────────────────────────────────────

/**
 * Returns a function to invalidate the courier-routes cache.
 * Call after any mutation that changes box statuses, signatures, or order composition.
 *
 * Uses invalidateQueries (marks stale + background refetch) rather than
 * removeQueries, so the UI can render stale data instantly while fresh data loads.
 */
export function useCourierCacheInvalidation() {
  const queryClient = useQueryClient();
  const today = useMemo(() => DateTime.now().toFormat('yyyy-MM-dd'), []);

  const invalidateCourierCache = useCallback(() => {
    // Skip invalidation when offline — the mutation was queued, not applied on server,
    // so a refetch would just return the old server state. Caches will be invalidated
    // after the offline queue syncs successfully (Layout.tsx / Connectivity.tsx).
    if (!navigator.onLine) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.courierRoutes(today) });
  }, [queryClient, today]);

  return { invalidateCourierCache };
}

/**
 * Returns a function to invalidate the warehouse-incoming cache.
 * Call after mutations that change box statuses on the incoming flow (ITKK→WKKU→WKKS).
 */
export function useWarehouseIncomingInvalidation() {
  const queryClient = useQueryClient();

  const invalidateWarehouseIncoming = useCallback((date?: string) => {
    if (!navigator.onLine) return;
    if (date) {
      queryClient.invalidateQueries({ queryKey: queryKeys.warehouseIncoming(date) });
    } else {
      queryClient.invalidateQueries({ queryKey: ['warehouse-incoming'] });
    }
  }, [queryClient]);

  return { invalidateWarehouseIncoming };
}

/**
 * Returns a function to invalidate the warehouse-outgoing cache.
 * Call after mutations that change box statuses on the outgoing flow (WKKS→WKKP→WKKHO→ITTC).
 */
export function useWarehouseOutgoingInvalidation() {
  const queryClient = useQueryClient();

  const invalidateWarehouseOutgoing = useCallback((date?: string) => {
    if (!navigator.onLine) return;
    if (date) {
      queryClient.invalidateQueries({ queryKey: queryKeys.warehouseOutgoing(date) });
    } else {
      queryClient.invalidateQueries({ queryKey: ['warehouse-outgoing'] });
    }
  }, [queryClient]);

  return { invalidateWarehouseOutgoing };
}
